/**
 * Map Interaction Helpers
 *
 * Utilities for managing MapLibre interaction behaviors,
 * particularly for mobile double-tap zoom prevention.
 *
 * @module utils/mapInteractionHelpers
 */

import type { Map as MapLibreMap } from 'maplibre-gl';
import { DOUBLE_TAP_ZOOM_PROTECTION_DURATION } from '../constants';

/**
 * Return type for createDoubleTapZoomProtection function
 */
export interface DoubleTapZoomProtection {
  /** Call to temporarily disable double-click zoom */
  protect: () => void;
  /** Call on component unmount to cleanup and restore zoom */
  cleanup: () => void;
}

/**
 * Creates a double-tap zoom protection controller for a map instance.
 *
 * Use this to prevent MapLibre's double-tap zoom when user double-taps
 * on geometry elements (markers, lines, shapes) to enter edit mode.
 *
 * The protection works by:
 * 1. Immediately disabling doubleClickZoom on protect() call
 * 2. Auto-enabling after DOUBLE_TAP_ZOOM_PROTECTION_DURATION timeout
 * 3. Properly cleaning up on component unmount
 *
 * @param map - MapLibre map instance (can be null during initialization)
 * @returns Object with protect() and cleanup() methods
 *
 * @example
 * ```typescript
 * // In a component with map access:
 * const protectionRef = useRef<DoubleTapZoomProtection | null>(null);
 *
 * // Initialize once when map is available
 * if (!protectionRef.current && map) {
 *   protectionRef.current = createDoubleTapZoomProtection(map);
 * }
 *
 * // On touchstart on geometry:
 * protectionRef.current?.protect();
 *
 * // On component unmount:
 * useEffect(() => {
 *   return () => protectionRef.current?.cleanup();
 * }, []);
 * ```
 */
export function createDoubleTapZoomProtection(
  map: MapLibreMap | null
): DoubleTapZoomProtection {
  let timeoutId: number | null = null;

  const protect = () => {
    if (!map) return;

    // Clear any existing timeout (prevents multiple timers)
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }

    // Immediately disable double-click zoom
    map.doubleClickZoom.disable();

    // Auto-enable after timeout
    timeoutId = window.setTimeout(() => {
      map.doubleClickZoom.enable();
      timeoutId = null;
    }, DOUBLE_TAP_ZOOM_PROTECTION_DURATION);
  };

  const cleanup = () => {
    // Clear pending timeout
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }

    // Re-enable zoom on cleanup to restore normal behavior
    if (map) {
      map.doubleClickZoom.enable();
    }
  };

  return { protect, cleanup };
}
